// PLAYER'S PREFERRED LANGUAGE — CCT-279.
//
// The player picks a language; the home screen loads in it WHEN the mission offers it, and in
// English otherwise. Two simple rules — but both are easy to get backwards by accident:
//
//   * we do NOT fall back to the mission's source language. It can be anything, and a French
//     speaker would end up reading German because the author happens to be German. English is
//     the COMMON fallback, chosen once for everyone.
//   * we never "guess" a close language: `fr-CA` requested with `fr` available resolves (same
//     language), but `es` requested never becomes `pt`.

/** Common fallback when the requested language is not served. */
export const DEFAULT_LANGUAGE = 'en';

/** `FR-fr `, `fr_FR` -> `fr-fr`. Varying case would create two twin languages. */
export function normalizeLanguage(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/_/g, '-');
}

/** The LANGUAGE part of a locale: `fr-ca` -> `fr`. */
function base(locale: string): string {
  return normalizeLanguage(locale).split('-')[0] ?? '';
}

/**
 * Which language to serve, among those AVAILABLE, to a player who prefers `wanted`.
 *
 * ⚠️ ALWAYS RETURNS AN AVAILABLE LANGUAGE, or `null` when the list is empty. Returning the
 * wanted language when it is not served would make the app ask the server for content that
 * does not exist — and the screen would show nothing, with no way to tell why.
 */
export function languageToServe(wanted: unknown, available: unknown[]): string | null {
  const pool = (available ?? []).map(normalizeLanguage).filter(Boolean);
  if (pool.length === 0) return null;
  const w = normalizeLanguage(wanted);

  // 1. Exactly the requested language.
  if (w && pool.includes(w)) return w;
  // 2. Same language, different region: `fr-ca` asked for, `fr` available — and vice versa.
  if (w) {
    const b = base(w);
    const near = pool.find((d) => base(d) === b);
    if (near) return near;
  }
  // 3. The COMMON fallback. Not the first in the list: that would be the author's language.
  const byDefault = pool.find((d) => base(d) === DEFAULT_LANGUAGE);
  if (byDefault) return byDefault;
  // 4. Neither the wanted language nor English. We serve what exists rather than nothing —
  //    but this is the LAST resort, and it is spelled out as such.
  return pool[0] ?? null;
}

/**
 * The languages a mission can be PLAYED in.
 *
 * ⚠️ THIS IS NOT `mission.langue`. That one says which language the story was WRITTEN in.
 * Measured against the database: all twenty missions declared `fr` — so the catalogue showed
 * ONE language, the language bar never appeared (it only shows from two upwards) and the
 * filter stayed invisible, while Ego Crisis was fully translated into English and Spanish.
 *
 * The server now serves `languages` (source + COMPLETE translations). The fallback on the
 * single language is kept: a server not yet deployed still tells the truth it knows.
 */
export function missionLanguages(
  m: {
    /** ENGLISH names — the ones the contract is migrating to (CCT-283). */
    language?: string | null;
    languages?: readonly (string | null | undefined)[] | null;
    /** ⚠️ FRENCH NAMES KEPT FOR THE LENGTH OF THE COMPATIBILITY WINDOW. A deployment is never
     *  atomic: between the server switching over and the app following, there is an interval
     *  where only the old key arrives. Reading both is what makes the rename possible WITHOUT
     *  downtime — and it is exactly what `plateformes` lacked, where the front read an empty
     *  list with no error and no trace. */
    langue?: string | null;
    langues?: readonly (string | null | undefined)[] | null;
  } | null | undefined,
): string[] {
  const seen: string[] = [];
  // The NEW name first: when both arrive, it is the one that counts.
  for (const v of [...(m?.languages ?? m?.langues ?? []), m?.language ?? m?.langue]) {
    const n = normalizeLanguage(v);
    // THE ORDER COMES FROM THE SERVER (source first): it is read as-is on the mission card.
    if (n && !seen.includes(n)) seen.push(n);
  }
  return seen;
}

/**
 * Does the mission pass the `chosen` filter? Accepts a single language OR the list of
 * playable ones — a mission translated into Spanish must show up under the "ES" filter.
 *
 * ⚠️ AN EMPTY FILTER FILTERS NOTHING. Treating it as "no match" would empty the home screen
 * on the first render, before the stored preference has been read.
 *
 * ⚠️ A MISSION WITH NO DECLARED LANGUAGE STAYS VISIBLE. The column is recent: hiding those
 * would make the whole existing catalogue disappear, with nothing on screen to explain it.
 */
export function missionMatchesLanguage(missionLanguage: unknown, chosen: unknown): boolean {
  const c = normalizeLanguage(chosen);
  if (!c) return true;
  const languages = (Array.isArray(missionLanguage) ? missionLanguage : [missionLanguage])
    .map(normalizeLanguage).filter(Boolean);
  if (languages.length === 0) return true;
  return languages.some((m) => base(m) === base(c));
}
